import { NO, UIColor, UITextView, UIView, YES } from "uicore-ts"
import type { RHProcurement, RHProcurementPublic } from "../../../models/prodModels"
import { RHCore } from "../Core/RHCore"
import { CBDialogViewShower } from "../Custom components/CB/CBDialogViewShower"
import { RHFocusManager } from "../Custom components/RH/Keyboard navigation/RHFocusManager"
import { RHButton } from "../Custom components/RH/RHButton"
import { RHBadgeView } from "../Custom components/RH/RHBadgeView"
import { RHFilterChipBarView } from "../Custom components/RH/RHFilterChipBarView"
import { RHFilterChipButton } from "../Custom components/RH/RHFilterChipButton"
import { RHButtons, RHColors, RHFont, RHSpacing } from "../Custom components/RH/Utils_slop/RHDesignTokens"
import { RHProcurementLifecyclePresentation } from "../Custom components/RH/Utils_slop/RHProcurementLifecyclePresentation"
import { RHProcurementPartDisplay } from "../Custom components/RH/Utils_slop/RHProcurementPartDisplay"
import { RHTextSanitizer } from "../Custom components/RH/Utils_slop/RHTextSanitizer"
import type { RHOfferNotificationSnapshot } from "../SocketClientFunctions"
import { RHOffersEvaluationView } from "./EvaluateOffers/RHOffersEvaluationView"
import { MakeOfferView, type RHMakeOfferLoadResult } from "./MakeOffer/MakeOfferView"
import { ProcurementRoundContext, type ProcurementRound } from "./ProcurementRoundContext"
import { RHProcurementDetailsView } from "./RHProcurementDetailsView"
import { referenceId } from "../Core/RHReference"


export type ProcurementRoundTab = "details" | "offers" | "makeOffer"


export class ProcurementRoundWorkspaceView extends UIView {
    readonly identityLabel = new UITextView().configuredWithObject({
        textColor: RHColors.textSecondary,
        style: { fontFamily: RHFont.familyBase, fontSize: RHFont.sizeMedium }
    }).addedAsSubviewToView(this)
    readonly sectionsByPartId = new Map<string, ProcurementRoundPartView>()
    roundSelectionDidRequest?: (round: ProcurementRound) => void
    amendmentDidRequest?: (procurementId: string) => void
    closureDidRequest?: (round: ProcurementRound) => void
    userDidRequestTendererRegistrationWithdrawal?: (procurementId: string, button: RHButton) => void
    reconciliationDidRequest?: (procurementId: string, partId: string) => void
    procurementDidChange?: () => void
    offerContentDidLoad?: (procurementId: string, snapshot: RHOfferNotificationSnapshot) => void
    offererContentDidLoad?: (round: ProcurementRound, snapshot: RHMakeOfferLoadResult) => void
    roundDetailsViewDidConfigure?: (detailsView: RHProcurementDetailsView, round: ProcurementRound) => void

    override _focusManager = new RHFocusManager(() =>
        [...this.sectionsByPartId.values()].map(section => section._focusManager)
    )

    constructor(elementID?: string) {
        super(elementID)
    }

    configureForContext(context: ProcurementRoundContext, tab: ProcurementRoundTab) {
        const rootInformation = context.rootInformation
        if (!rootInformation) {
            return
        }
        const root = rootInformation.procurement
        let status = ""
        if (rootInformation.lifecycleState) {
            status = RHProcurementLifecyclePresentation.detailsTextForLifecycleState(rootInformation.lifecycleState)
        }
        this.identityLabel.text = `${RHTextSanitizer.escapedHTML(root.referenceNumber ?? "")} · ${status}`
        for (const part of root.parts) {
            const partId = part._id.toString()
            const round = context.selectedRoundForPart(partId)
            if (!round) {
                continue
            }
            let section = this.sectionsByPartId.get(partId)
            if (!section) {
                section = new ProcurementRoundPartView(this).addedAsSubviewToView(this)
                this.sectionsByPartId.set(partId, section)
            }
            section.configureForRound(round, context, tab)
        }
        for (const [partId, section] of this.sectionsByPartId) {
            if (!context.roundsByPartId.has(partId)) {
                section.removeFromSuperview()
                this.sectionsByPartId.delete(partId)
            }
        }
        this.setNeedsLayoutUpToRootView()
    }

    get hasUnsavedOfferData(): boolean {
        return [...this.sectionsByPartId.values()].some(section => section.hasUnsavedOfferData)
    }

    get isOfferResubmissionRequired(): boolean {
        return [...this.sectionsByPartId.values()].some(section =>
            section._rounds.some(round => {
                if (section._context?.isHistoricalRound(round)) {
                    return NO
                }
                const content = section.contentByRoundAndTab.get(`${round.procurementId}:makeOffer`)
                return content instanceof MakeOfferView && content.isOfferResubmissionRequired
            })
        )
    }

    discardUnsavedChanges() {
        for (const section of this.sectionsByPartId.values()) {
            for (const content of section.contentByRoundAndTab.values()) {
                if (content instanceof MakeOfferView) {
                    content.discardUnsavedChanges()
                }
            }
        }
    }

    async saveUnsavedOfferDrafts(): Promise<boolean> {
        for (const section of this.sectionsByPartId.values()) {
            for (const content of section.contentByRoundAndTab.values()) {
                if (
                    content instanceof MakeOfferView &&
                    content.hasUnsavedOfferData &&
                    !await content.saveDraft()
                ) {
                    return NO
                }
            }
        }
        return YES
    }

    invalidateOfferContentForProcurements(procurementIds: string[]) {
        for (const section of this.sectionsByPartId.values()) {
            for (const [key, content] of section.contentByRoundAndTab) {
                if ((content instanceof MakeOfferView ||
                    (content instanceof RHOffersEvaluationView && !content.isMemberOfViewTree)) &&
                    (procurementIds.length === 0 || procurementIds.includes(content._procurement?._id?.toString() ?? ""))) {
                    section.informationByContentKey.delete(key)
                }
            }
        }
    }

    override layoutSubviews() {
        super.layoutSubviews()
        const bounds = this.contentBoundsWithInset(0)
        const sections = [...this.sectionsByPartId.values()]
        bounds.rectangleWithHeight(this.identityLabel)
            .assignedAsFrameOfView(this.identityLabel)
            .rectangleForNextRow(RHSpacing.large, 0)
            .framesByDistributingViewsAsColumn(sections, RHSpacing.large, sections)
    }
}


class ProcurementRoundPartView extends UIView {
    readonly titleLabel = new UITextView(undefined, UITextView.type.header4).configuredWithObject({
        textColor: RHColors.textPrimary,
        style: { fontFamily: RHFont.familyBase, fontWeight: "600" }
    }).addedAsSubviewToView(this)
    readonly statusBadge = new RHBadgeView().addedAsSubviewToView(this)
    readonly roundBar = new RHFilterChipBarView().configuredWithObject({ rowHeight: 36 }).addedAsSubviewToView(this)
    readonly stateLabel = new UITextView().configuredWithObject({
        isSingleLine: NO,
        textColor: RHColors.textSecondary,
        style: { fontFamily: RHFont.familyBase, fontSize: RHFont.sizeMedium }
    }).addedAsSubviewToView(this)
    readonly abandonButton = new RHButton().configuredWithObject({
        titleLabel: { text: "Loobu täpsustamisest" },
        variant: RHButtons.destructiveUtilityVariant,
        contentPadding: RHButtons.compactContentPadding,
        style: { fontSize: RHButtons.compactFontSize },
        controlEventTargetAccumulator: {
            PrimaryActionTriggered: () => this._confirmReconciliationAbandonment()
        }
    })
    readonly contentByRoundAndTab = new Map<string, RHProcurementDetailsView | RHOffersEvaluationView | MakeOfferView>()
    readonly informationByContentKey = new Map<string, ProcurementRound["information"]>()
    _round?: ProcurementRound
    _rounds: ProcurementRound[] = []
    _context?: ProcurementRoundContext
    _content?: UIView

    override _focusManager = new RHFocusManager(() => {
        const items: (UIView | RHFocusManager)[] = [...this.roundBar.chips]
        if (this._content?._focusManager) {
            items.push(this._content._focusManager)
        }
        if (this._content instanceof RHProcurementDetailsView &&
            !this._content.tendererRegistrationWithdrawalButton.hidden) {
            items.push(this._content.tendererRegistrationWithdrawalButton)
        }
        if (this._content instanceof RHProcurementDetailsView &&
            this._content.showsOpeningProtocolExport &&
            !this._content.showsOpeningProtocolUnavailableExplanation) {
            items.push(this._content.openingProtocolExportButtonView.button)
            if (!this._content.openingProtocolShareButton.hidden) {
                items.push(this._content.openingProtocolShareButton)
            }
        }
        return items as UIView[]
    })

    constructor(readonly workspace: ProcurementRoundWorkspaceView) {
        super()
        ;(this as ProcurementRoundPartView).configureWithObject({
            backgroundColor: UIColor.whiteColor,
            style: {
                border: `1px solid ${RHColors.borderSlate.stringValue}`,
                borderRadius: "14px",
                boxShadow: "0 3px 12px rgba(15, 23, 42, 0.06)"
            }
        })
        this.statusBadge.label.configureWithObject({
            style: { fontFamily: RHFont.familyBase, fontSize: RHFont.sizeMedium, fontWeight: "700", boxSizing: "border-box" }
        })
        this.roundBar.viewHTMLElement.setAttribute("role", "group")
    }

    configureForRound(round: ProcurementRound, context: ProcurementRoundContext, tab: ProcurementRoundTab) {
        this._round = round
        this._context = context
        this._rounds = context.roundsByPartId.get(round.rootPartId) ?? []
        const rootProcurement = context.rootInformation!.procurement
        const rootPart = rootProcurement.parts.find(candidate => candidate._id.toString() === round.rootPartId)!
        const showsPartIdentity = rootProcurement.parts.length > 1
        this.titleLabel.text = "Hanke voorud"
        if (showsPartIdentity) {
            this.titleLabel.text = `Osa ${rootPart.partNumber}`
        }
        let accessibleRoundLabel = "Voor"
        if (showsPartIdentity) {
            accessibleRoundLabel = `${this.titleLabel.text} — voor`
        }
        this.roundBar.viewHTMLElement.setAttribute("aria-label", accessibleRoundLabel)
        const latestRound = this._rounds[this._rounds.length - 1]
        const isLatestRoundHistorical = context.isHistoricalRound(latestRound)
        this.statusBadge.text = `${ProcurementRoundContext.titleForRound(latestRound)} käimas`
        this.statusBadge.setVariant(RHColors.warningAmberSurfaceDraft, RHColors.warningAmberText)
        if (isLatestRoundHistorical) {
            this.statusBadge.text = "Voor lõppenud"
            const currentProcurement = latestRound.information.procurement
            if ((currentProcurement.finalizedPartIds ?? []).includes(latestRound.partId) ||
                currentProcurement.status === "evaluation-finalized") {
                this.statusBadge.text = "Lõpetatud"
            }
            if (currentProcurement.status === "draft") {
                this.statusBadge.text = "Ettevalmistamisel"
            }
            else if (currentProcurement.status === "draft-cancelled") {
                this.statusBadge.text = "Tühistatud"
            }
            this.statusBadge.setVariant(RHColors.successGreenSurfaceLight, RHColors.successGreenTextDeep)
            if (currentProcurement.status === "draft" || currentProcurement.status === "draft-cancelled") {
                this.statusBadge.setVariant(RHColors.surfaceCool, RHColors.textSecondary)
            }
        }
        this.roundBar.chips = this._rounds.map(candidate => {
            let title = ProcurementRoundContext.titleForRound(candidate)
            if (context.isHistoricalRound(candidate)) {
                title = `✓ ${title}`
            }
            else {
                title = `${title} (käimas)`
            }
            const button = new RHFilterChipButton().configuredWithObject({
                chipSize: "large",
                titleLabel: { text: title },
                chipActive: candidate.procurementId === round.procurementId,
                controlEventTargetAccumulator: {
                    PrimaryActionTriggered: () => this.workspace.roundSelectionDidRequest?.(candidate)
                }
            })
            button.viewHTMLElement.setAttribute("aria-label", ProcurementRoundContext.titleForRound(candidate))
            button.viewHTMLElement.setAttribute("aria-pressed", String(candidate.procurementId === round.procurementId))
            if (candidate === latestRound && !isLatestRoundHistorical) {
                button.viewHTMLElement.setAttribute("aria-description", "Käimasolev voor")
            }
            return button
        })
        const isHistorical = context.isHistoricalRound(round)
        const procurement = round.information.procurement
        const procurementForDisplay = { ...procurement, title: rootProcurement.title, referenceNumber: rootProcurement.referenceNumber }
        if (round.information.isClosedWithoutContractInReconciliationChain === YES) {
            procurementForDisplay.status = "closed-without-contract"
        }
        let state = RHProcurementLifecyclePresentation.textForLifecycleState("started")
        if (isHistorical) {
            state = "Ajalooline voor · ainult lugemiseks"
        }
        else if (procurement.offersOpenedAt) {
            state = "Pakkumused avatud · hindamine"
        }
        else if (procurement.deadlineDate && new Date(procurement.deadlineDate).getTime() <= Date.now()) {
            state = "Esitamise tähtaeg möödunud · pakkumused ootavad avamist"
        }
        this.stateLabel.text = `${ProcurementRoundContext.titleForRound(round)} · ${state}`
        this.abandonButton.removeFromSuperview()
        const reconciliationParentId = referenceId(procurement.reconciliationParentProcurement)
        const reconciliationParentRound = this._rounds.find(candidate => candidate.procurementId === reconciliationParentId)
        const canAbandonReconciliation = tab === "offers" && !isHistorical &&
            reconciliationParentRound?.information.capabilities?.canFinalizeOrCancel === YES
        const contentKey = `${round.procurementId}:${tab}`
        let content = this.contentByRoundAndTab.get(contentKey)
        if (!content) {
            if (tab === "details") {
                content = new RHProcurementDetailsView().configuredWithObject({
                    backgroundColor: UIColor.transparentColor,
                    style: { border: "none", boxShadow: "none" },
                    showsProcurementIdentity: NO,
                    displayedPartId: round.partId,
                    userDidRequestProcurementModification: () => this.workspace.amendmentDidRequest?.(round.procurementId),
                    userDidRequestProcurementClosureWithoutContract: () => this.workspace.closureDidRequest?.(round),
                    userDidRequestTendererRegistrationWithdrawal: () => {
                        const detailsView = this._content
                        if (detailsView instanceof RHProcurementDetailsView) {
                            this.workspace.userDidRequestTendererRegistrationWithdrawal?.(
                                round.procurementId,
                                detailsView.tendererRegistrationWithdrawalButton
                            )
                        }
                    },
                    userDidRequestPartClosureWithoutContract: () => this.workspace.closureDidRequest?.(round)
                })
            }
            else if (tab === "offers") {
                content = new RHOffersEvaluationView().configuredWithObject({
                    displayedPartId: round.partId,
                    createReconciliationDidRequest: this.workspace.reconciliationDidRequest,
                    cancelPartDidRequest: () => this.workspace.closureDidRequest?.(round),
                    offersWereOpened: this.workspace.procurementDidChange,
                    partEvaluationDidFinalize: this.workspace.procurementDidChange,
                    evaluationContentDidLoad: this.workspace.offerContentDidLoad
                })
            }
            else {
                content = new MakeOfferView().configuredWithObject({
                    displayedPartId: round.partId,
                    didSubmitProcurementOffer: () => this.workspace.procurementDidChange?.()
                })
            }
            this.contentByRoundAndTab.set(contentKey, content)
        }
        if (this._content !== content) {
            this._content?.removeFromSuperview()
            this._content = content.addedAsSubviewToView(this)
        }
        const capabilities = round.information.capabilities
        const canMutate = !isHistorical && round.information.isCurrentUserOnProcuringSide && RHCore.sharedInstance.applicationMode === "procurer"
        if (content instanceof RHProcurementDetailsView) {
            content.configureWithCurrentUserTeamRole(capabilities?.teamRole)
            content.configureWithObject({
                numberOfRootProcurementParts: rootProcurement.parts.length,
                isOffererView: RHCore.sharedInstance.isInOffererMode && !round.information.isCurrentUserOnProcuringSide,
                canModifyPublishedProcurement: canMutate && capabilities?.canEdit === YES && !procurement.offersOpenedAt,
                canCloseProcurementWithoutContract: canMutate && capabilities?.canFinalizeOrCancel === YES && procurement.parts.length === 1,
                canCloseProcurementPartsWithoutContract: canMutate && capabilities?.canFinalizeOrCancel === YES && procurement.parts.length > 1
            })
            content.procurementData = procurementForDisplay
            this.workspace.roundDetailsViewDidConfigure?.(content, round)
            content.shareButton.hidden = YES
            content.tendererRegistrationWithdrawalButton.hidden = isHistorical ||
                !RHCore.sharedInstance.isInOffererMode || !round.information.canWithdrawTendererRegistration
        }
        else {
            const wasReadOnly = content.isReadOnly
            if (content instanceof RHOffersEvaluationView) {
                content.configureWithObject({
                    isReadOnly: context.hasNextRound(round) ||
                        round.information.isClosedWithoutContractInReconciliationChain === YES ||
                        !["published", "evaluation-finalized"].includes(procurement.status) ||
                        (procurement.closedWithoutContractPartIds ?? []).includes(round.partId) ||
                        (procurement.reconciliationLockedPartIds ?? []).includes(round.partId),
                    hasNextRound: context.hasNextRound(round),
                    numberOfRootProcurementParts: rootProcurement.parts.length,
                    isPartClosedWithoutContractInRoundHistory: this._rounds.some(candidate =>
                        candidate.information.procurement.status === "closed-without-contract" ||
                        (candidate.information.procurement.closedWithoutContractPartIds ?? []).includes(candidate.partId)
                    )
                })
                if (isHistorical && !content.isReadOnly) {
                    this.stateLabel.text = `${ProcurementRoundContext.titleForRound(round)} · Hindamine on lõpetatud`
                }
            }
            else {
                content.configureWithObject({
                    isReadOnly: isHistorical,
                    hasNextRound: context.hasNextRound(round)
                })
            }
            const hasReadOnlyStateChanged = content.isReadOnly !== wasReadOnly
            if (hasReadOnlyStateChanged && content instanceof RHOffersEvaluationView) {
                content._clearPartSections()
                content.openOffersButton.removeFromSuperview()
            }
            if (content instanceof RHOffersEvaluationView) {
                content.procurementCapabilities = capabilities
                content.additionalActionButton = undefined
                if (canAbandonReconciliation) {
                    content.additionalActionButton = this.abandonButton
                }
            }
            if (isHistorical && content instanceof MakeOfferView) {
                content.offerActionsEnabled = NO
                content.enterView.removeFromSuperview()
                content.confirmView.removeFromSuperview()
                content.submittedView.removeFromSuperview()
                content.discardUnsavedChanges()
            }
            const hasUnsavedData = content instanceof MakeOfferView &&
                content.activeStepView === content.enterView &&
                content.enterView.hasSections && content.enterView.hasUnsavedSessionData
            if (hasUnsavedData && !isHistorical && content instanceof MakeOfferView) {
                content.updateRequiredDocumentsForProcurement(procurementForDisplay)
            }
            if ((!hasUnsavedData || isHistorical) &&
                (hasReadOnlyStateChanged || this.informationByContentKey.get(contentKey) !== round.information)) {
                const previousInformation = this.informationByContentKey.get(contentKey)
                this.informationByContentKey.set(contentKey, round.information)
                if (content instanceof RHOffersEvaluationView && previousInformation && !hasReadOnlyStateChanged &&
                    previousInformation.capabilities?.canEvaluate === capabilities?.canEvaluate &&
                    previousInformation.capabilities?.canSelectWinner === capabilities?.canSelectWinner &&
                    previousInformation.capabilities?.canFinalizeOrCancel === capabilities?.canFinalizeOrCancel &&
                    previousInformation.procurement.offersOpenedAt === procurement.offersOpenedAt) {
                    content.refreshProcurementMetadata(procurementForDisplay as RHProcurement)
                    this.setNeedsLayoutUpToRootView()
                    return
                }
                if (content instanceof MakeOfferView) {
                    content.offerContentDidLoad = result => {
                        if (this._content === content && this._round === round && this.workspace.superview) {
                            this.workspace.offererContentDidLoad?.(round, result)
                        }
                    }
                    void content.loadForProcurement(procurementForDisplay, rootProcurement.parts.length)
                        .catch(error => {
                            this.informationByContentKey.delete(contentKey)
                            console.error("Round content load failed:", error)
                            CBDialogViewShower.alert("Vooru andmete laadimine ebaõnnestus. Palun proovi uuesti.")
                        })
                }
                else {
                    void content.loadForProcurement(procurementForDisplay as RHProcurement)
                        .catch(error => {
                            this.informationByContentKey.delete(contentKey)
                            console.error("Round content load failed:", error)
                            CBDialogViewShower.alert("Vooru andmete laadimine ebaõnnestus. Palun proovi uuesti.")
                        })
                }
            }
        }
        this.setNeedsLayoutUpToRootView()
    }

    get hasUnsavedOfferData(): boolean {
        return [...this.contentByRoundAndTab.values()].some(content =>
            content instanceof MakeOfferView && content.hasUnsavedOfferData
        )
    }

    _confirmReconciliationAbandonment() {
        const round = this._round
        if (!round || !this._context || this._context.isHistoricalRound(round)) {
            return
        }
        const reconciliationParentId = referenceId(round.information.procurement.reconciliationParentProcurement)
        const reconciliationParentRound = this._rounds.find(candidate => candidate.procurementId === reconciliationParentId)
        if (!reconciliationParentRound || reconciliationParentRound.information.capabilities?.canFinalizeOrCancel !== YES) {
            return
        }
        let questionText = "Kas soovid täpsustamisest loobuda? Hanke hindamine lõpeb ilma võitjata ja seda ei saa hiljem muuta."
        if (RHProcurementPartDisplay.shouldShowPartIdentityForPartCount(this._context.rootInformation?.procurement.parts.length)) {
            questionText = "Kas soovid täpsustamisest loobuda? Selle osa hindamine lõpeb ilma võitjata ja seda ei saa hiljem muuta."
        }
        const shower = CBDialogViewShower.showQuestionDialog(questionText)
        shower.yesButtonWasPressed = async () => {
            shower.dialogView.dismiss()
            this.abandonButton.enabled = NO
            try {
                await RHCore.sharedInstance.offerStore.abandonReconciliationForPart(reconciliationParentRound.procurementId, reconciliationParentRound.partId)
                this.workspace.procurementDidChange?.()
            }
            catch (error) {
                console.error("Reconciliation abandonment failed:", error)
                CBDialogViewShower.alert("Täpsustamisest loobumine ebaõnnestus.")
            }
            finally {
                this.abandonButton.enabled = YES
            }
        }
    }

    override layoutSubviews() {
        super.layoutSubviews()
        const bounds = this.contentBoundsWithInset(RHSpacing.large)
        bounds.rectangleWithHeight(this.statusBadge)
            .IF(this.titleLabel.intrinsicContentWidth(32) + this.statusBadge.intrinsicContentWidth(32) + RHSpacing.medium <= bounds.width)
            .distributeViewsAlongWidth([this.titleLabel, this.statusBadge], undefined, RHSpacing.medium, [
                bounds.width - this.statusBadge.intrinsicContentWidth(32) - RHSpacing.medium,
                this.statusBadge.intrinsicContentWidth(32)
            ])
            .ELSE()
            .rectangleWithHeight(this.titleLabel)
            .assignedAsFrameOfView(this.titleLabel)
            .rectangleForNextRow(RHSpacing.small, this.statusBadge)
            .rectangleWithWidth(this.statusBadge)
            .assignedAsFrameOfView(this.statusBadge)
            .ENDIF()
            .rectangleWithWidth(bounds.width)
            .rectangleForNextRow(RHSpacing.medium, this.roundBar)
            .assignedAsFrameOfView(this.roundBar)
            .rectangleForNextRow(RHSpacing.small, this.stateLabel)
            .assignedAsFrameOfView(this.stateLabel)
            .IF(!!this._content)
            .rectangleForNextRow(RHSpacing.medium, this._content!)
            .assignedAsFrameOfView(this._content!)
            .ENDIF()
    }
}
